/*
  Vimeo Facade — lightweight click-to-play embed for UGC carousel.
  Zero Vimeo JS loaded until a user taps play.
*/

let vimeoSDKPromise = null;
function loadVimeoSDK() {
  if (vimeoSDKPromise) return vimeoSDKPromise;
  vimeoSDKPromise = new Promise((resolve, reject) => {
    if (window.Vimeo && window.Vimeo.Player) return resolve();
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return vimeoSDKPromise;
}

const BRAND_COLOR = "#0038C7";

const PLAY_SVG = `<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="68" height="48" rx="10" fill="${BRAND_COLOR}" fill-opacity="0.9"/>
  <path d="M45 24L28 33.5V14.5L45 24Z" fill="white"/>
</svg>`;

const LOADING_SVG = `<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="68" height="48" rx="10" fill="${BRAND_COLOR}" fill-opacity="0.9"/>
  <circle cx="34" cy="24" r="10" stroke="white" stroke-width="2.5" stroke-linecap="round" fill="none" stroke-dasharray="48" stroke-dashoffset="16">
    <animateTransform attributeName="transform" type="rotate" from="0 34 24" to="360 34 24" dur="0.8s" repeatCount="indefinite"/>
  </circle>
</svg>`;

const PAUSE_SVG = `<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="68" height="48" rx="10" fill="${BRAND_COLOR}" fill-opacity="0.9"/>
  <rect x="25" y="14" width="7" height="20" rx="1.5" fill="white"/>
  <rect x="36" y="14" width="7" height="20" rx="1.5" fill="white"/>
</svg>`;

class VimeoFacade extends HTMLElement {
  connectedCallback() {
    this.vimeoId = this.dataset.vimeoId;
    if (!this.vimeoId) return;

    this._isPlaying = false;
    this._player = null;

    // Only fetch Vimeo thumbnail if no server-rendered poster image exists
    if (this.querySelector(".vimeo-facade__poster")) {
      this.fetchThumbnail();
    }
    this.addEventListener("click", () => this.activate(), { once: true });
  }

  async fetchThumbnail() {
    try {
      const resp = await fetch(
        `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${this.vimeoId}&width=480`,
      );
      const data = await resp.json();
      const poster = this.querySelector(".vimeo-facade__poster");
      if (poster && data.thumbnail_url) {
        const portraitThumb = data.thumbnail_url
          .replace(/_\d+x\d+/, "_540x960")
          .replace(/\?mw=\d+/, "?mw=540");
        poster.style.backgroundImage = `url(${portraitThumb})`;
      }
    } catch (e) {
      /* black background fallback */
    }
  }

  async activate() {
    const playBtn = this.querySelector(".vimeo-facade__play");
    if (playBtn) playBtn.innerHTML = LOADING_SVG;

    document.querySelectorAll("vimeo-facade.is-active").forEach((el) => {
      if (el !== this && el._player) el._player.pause();
    });

    await loadVimeoSDK();

    const iframe = document.createElement("iframe");
    iframe.src = `https://player.vimeo.com/video/${this.vimeoId}?background=1&dnt=1`;
    iframe.className = "absolute";
    iframe.style.cssText =
      "pointer-events: none; opacity: 0; top: 50%; left: 50%; width: 110%; height: 110%; transform: translate(-50%, -50%);";
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allow", "autoplay; fullscreen");
    iframe.setAttribute("allowfullscreen", "");

    this.insertBefore(iframe, this.firstChild);

    this._player = new Vimeo.Player(iframe);
    this._player.setVolume(1);
    this._player.setLoop(false);

    this._player.on("playing", () => {
      if (iframe.style.opacity === "0") {
        iframe.style.opacity = "1";
        this._revealOverlay(iframe);
      }
    });

    this._player.on("play", () => {
      setTimeout(() => {
        if (iframe.style.opacity === "0") {
          iframe.style.opacity = "1";
          this._revealOverlay(iframe);
        }
      }, 200);
    });

    this.classList.add("is-active");
  }

  _revealOverlay(iframe) {
    if (!this._originalHTML) {
      const facadeEls = Array.from(this.children).filter(
        (el) => el !== iframe,
      );
      const temp = document.createElement("div");
      facadeEls.forEach((el) => temp.appendChild(el.cloneNode(true)));
      this._originalHTML = temp.innerHTML;
    }

    Array.from(this.children).forEach((el) => {
      if (el !== iframe) el.remove();
    });

    const overlay = document.createElement("div");
    overlay.className = "absolute inset-0 z-10 cursor-pointer";
    overlay.style.cssText = "-webkit-tap-highlight-color: transparent;";

    const iconWrap = document.createElement("div");
    iconWrap.className =
      "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-200";
    iconWrap.innerHTML = PAUSE_SVG;
    overlay.appendChild(iconWrap);

    overlay.addEventListener("mouseenter", () => {
      iconWrap.style.opacity = "1";
    });
    overlay.addEventListener("mouseleave", () => {
      iconWrap.style.opacity = "0";
    });

    this.appendChild(overlay);
    this._isPlaying = true;
    this.classList.remove("cursor-pointer", "group");

    this._player.on("play", () => {
      this._isPlaying = true;
      iconWrap.innerHTML = PAUSE_SVG;
    });

    this._player.on("pause", () => {
      this._isPlaying = false;
      iconWrap.innerHTML = PLAY_SVG;
      iconWrap.style.opacity = "1";
    });

    this._player.on("ended", () => {
      this._isPlaying = false;
      iconWrap.innerHTML = PLAY_SVG;
      iconWrap.style.opacity = "1";
    });

    overlay.addEventListener("click", () => {
      if (this._isPlaying) {
        this._player.pause();
      } else {
        document.querySelectorAll("vimeo-facade.is-active").forEach((el) => {
          if (el !== this && el._player) el._player.pause();
        });
        this._player.play();
      }
    });
  }

  deactivate() {
    if (this._originalHTML) {
      if (this._player) {
        this._player.pause();
        this._player = null;
      }
      this.innerHTML = this._originalHTML;
      this._originalHTML = null;
      this.classList.remove("is-active");
      this.classList.add("cursor-pointer", "group");
      this.fetchThumbnail();
      this.addEventListener("click", () => this.activate(), { once: true });
    }
  }
}

if (!customElements.get("vimeo-facade")) {
  customElements.define("vimeo-facade", VimeoFacade);
}
